import { validateHttpsUrl, validateRequired } from '@workspace/seedforge-shared'
import { Checkbox } from '@workspace/ui/components/checkbox'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { Input } from '@workspace/ui/components/input'
import { Textarea } from '@workspace/ui/components/textarea'
import { cn } from '@workspace/ui/lib/utils'
import { useId, useState } from 'react'
import type { ExtractedInvestorData, LinkedInProfile, WebsiteData } from '../lib/api.js'
import { OnboardingLayout } from './onboarding-layout.js'

interface EnumOption {
  value: string
  label: string
}

interface InvestorProfileReviewProps {
  linkedInProfile: LinkedInProfile | null
  websiteData: WebsiteData | null
  extractedData: ExtractedInvestorData | null
  /** The website URL the user entered in the onboarding form */
  websiteUrl: string | null
  /** The signed-in user's name (e.g. from Google) — a fallback when there's
   * no LinkedIn scrape to name them from. */
  defaultUserName?: string
  error: string | null
  /** Available investment area options */
  areaOptions?: EnumOption[]
  /** Available investment region options */
  regionOptions?: EnumOption[]
  onConfirm: (data: {
    fundName: string
    fundWebsite: string
    description: string
    investmentAreas: string[]
    investmentGeographies: string[]
    userName: string
    position: string
  }) => void
}

// ─── Sub-components (matching ProfileEditForm style) ──────────────────────

function FormSection({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
      </div>
      {children}
    </div>
  )
}

function Field({
  label,
  children,
  className,
  error,
}: {
  label: string
  children: React.ReactNode
  className?: string
  error?: string
}) {
  return (
    <div
      className={cn(
        'space-y-1.5',
        error &&
          '[&_input]:ring-destructive/50 [&_input]:ring-[1.5px] [&_textarea]:ring-destructive/50 [&_textarea]:ring-[1.5px]',
        className
      )}
    >
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────

export function InvestorProfileReview({
  linkedInProfile,
  websiteData,
  extractedData,
  websiteUrl,
  defaultUserName,
  error,
  areaOptions = [],
  regionOptions = [],
  onConfirm,
}: InvestorProfileReviewProps) {
  // Unique per mount, so a label never points at another copy of the same option.
  const optionId = useId()
  const [fundName, setFundName] = useState(
    extractedData?.fundName ?? websiteData?.fundName ?? websiteData?.companyName ?? ''
  )
  const [fundWebsite, setFundWebsite] = useState(websiteUrl ?? '')
  const [description, setDescription] = useState(
    extractedData?.description ?? websiteData?.description ?? ''
  )
  const [investmentAreas, setInvestmentAreas] = useState<string[]>(
    extractedData?.investmentAreas ?? websiteData?.investmentAreas ?? []
  )
  const [investmentGeographies, setInvestmentGeographies] = useState<string[]>(
    extractedData?.investmentGeographies ?? websiteData?.investmentGeographies ?? []
  )
  const [userName, setUserName] = useState(linkedInProfile?.name ?? defaultUserName ?? '')
  const [position, setPosition] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({})

  const validateField = (field: string, value: string) => {
    let error: string | null = null
    if (field === 'fundName') error = validateRequired(value, 'Fund name')
    else if (field === 'userName') error = validateRequired(value, 'Name')
    else if (field === 'fundWebsite') error = validateHttpsUrl(value)
    setFieldErrors((prev) => ({ ...prev, [field]: error }))
    return error
  }

  const toggleArea = (value: string) => {
    setInvestmentAreas((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    )
  }

  const toggleRegion = (value: string) => {
    setInvestmentGeographies((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    )
  }

  const handleConfirm = () => {
    const errors = {
      fundName: validateRequired(fundName, 'Fund name'),
      userName: validateRequired(userName, 'Name'),
      fundWebsite: validateHttpsUrl(fundWebsite),
    }
    setFieldErrors(errors)
    if (Object.values(errors).some(Boolean)) return

    onConfirm({
      fundName,
      fundWebsite,
      description,
      investmentAreas,
      investmentGeographies,
      userName,
      position,
    })
  }

  return (
    <OnboardingLayout error={error} maxWidth="max-w-4xl">
      <div className="relative">
        {/* Form content */}
        <div className="space-y-10 py-8">
          {/* ── Personal Info ── */}
          <FormSection title="Personal Info" description="Your details as a fund representative">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Your name" error={fieldErrors.userName ?? undefined}>
                <Input
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  onBlur={() => validateField('userName', userName)}
                  placeholder="Jane Smith"
                />
              </Field>
              <Field label="Position">
                <Input
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  placeholder="General Partner"
                />
              </Field>
            </div>
          </FormSection>

          <hr className="border-border" />

          {/* ── Fund Details ── */}
          <FormSection title="Fund Details" description="Basic information about your fund">
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Fund name" error={fieldErrors.fundName ?? undefined}>
                  <Input
                    value={fundName}
                    onChange={(e) => setFundName(e.target.value)}
                    onBlur={() => validateField('fundName', fundName)}
                    placeholder="Acme Ventures"
                  />
                </Field>
                <Field label="Fund website" error={fieldErrors.fundWebsite ?? undefined}>
                  <Input
                    value={fundWebsite}
                    onChange={(e) => setFundWebsite(e.target.value)}
                    onBlur={() => validateField('fundWebsite', fundWebsite)}
                    placeholder="https://acmeventures.com"
                  />
                </Field>
              </div>
              <Field label="Description">
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of your fund's focus..."
                  rows={4}
                />
              </Field>
            </div>
          </FormSection>

          <hr className="border-border" />

          {/* ── Investment Areas ── */}
          <FormSection
            title="Investment Areas"
            description="Select the sectors you're interested in"
          >
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {areaOptions.map((area) => (
                <label
                  key={area.value}
                  htmlFor={`${optionId}-area-${area.value}`}
                  className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 transition-colors hover:bg-secondary"
                >
                  <Checkbox
                    id={`${optionId}-area-${area.value}`}
                    checked={investmentAreas.includes(area.value)}
                    onCheckedChange={() => toggleArea(area.value)}
                  />
                  <span className="text-sm text-foreground">{area.label}</span>
                </label>
              ))}
            </div>
          </FormSection>

          <hr className="border-border" />

          {/* ── Investment Geographies ── */}
          <FormSection
            title="Investment Geographies"
            description="Select the regions you invest in"
          >
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {regionOptions.map((region) => (
                <label
                  key={region.value}
                  htmlFor={`${optionId}-region-${region.value}`}
                  className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 transition-colors hover:bg-secondary"
                >
                  <Checkbox
                    id={`${optionId}-region-${region.value}`}
                    checked={investmentGeographies.includes(region.value)}
                    onCheckedChange={() => toggleRegion(region.value)}
                  />
                  <span className="text-sm text-foreground">{region.label}</span>
                </label>
              ))}
            </div>
          </FormSection>
        </div>

        {/* Sticky action bar (matching ProfileEditForm) */}
        <div className="sticky bottom-4 z-10 mt-8 rounded-xl border border-border/50 backdrop-blur-md">
          <div className="flex items-center justify-between px-6 py-3">
            <p className="text-sm text-muted-foreground">
              {extractedData || websiteData
                ? 'We pre-filled what we could. You can edit everything later.'
                : 'Fill in your fund details. You can always update these later.'}
            </p>
            <CtaButton size="sm" onClick={handleConfirm}>
              Looks good
            </CtaButton>
          </div>
        </div>
      </div>
    </OnboardingLayout>
  )
}
